import { fetchAllProducts, fetchMetabjects } from 'libs/shopify-product'
import { normalizeProducts } from 'libs/shopify-normalize'

export const GLOBAL_SWATCH_CACHE_KEY = 'globalSwatchDatas'

let cacheBuildPromise = null

const isProdShop = () =>
  window.Shopify.shop === 'nutraorganics.myshopify.com' ||
  window.Shopify.shop === 'nutra-organics-us.myshopify.com'

const groupProductsByLinkedCode = (products) => {
  const groups = products.reduce((acc, product) => {
    const linkedCode = product.linkedCode

    if (!linkedCode) return acc

    if (acc[linkedCode]) {
      acc[linkedCode].products.push(product)
    } else {
      acc[linkedCode] = { 'linked-code': linkedCode, products: [product] }
    }

    return acc
  }, {})

  return Object.values(groups)
}

const buildGlobalSwatchCache = async () => {
  let hasNextPage = true
  let endCursor = null
  let productsData = []

  while (hasNextPage) {
    const page = await fetchAllProducts(endCursor)

    productsData = productsData.concat(page?.nodes || [])
    hasNextPage = Boolean(page?.pageInfo?.hasNextPage)
    endCursor = page?.pageInfo?.endCursor || null
  }

  const normalized = normalizeProducts(productsData, 'shopify-graphql')
  const linkedGroup = groupProductsByLinkedCode(normalized)

  const metaobjectKey = isProdShop() ? 'flavour_name' : 'colour_name'
  const swatchResponse = await fetchMetabjects('swatches', metaobjectKey, 100)
  const swatches =
    swatchResponse?.map((item) => ({
      id: item.id,
      name: item.field?.value
    })) || []

  sessionStorage.setItem(
    GLOBAL_SWATCH_CACHE_KEY,
    JSON.stringify({
      linkedGroup,
      swatches
    })
  )
}

export const getGlobalSwatchDatas = () => {
  const cached = sessionStorage.getItem(GLOBAL_SWATCH_CACHE_KEY)

  if (!cached) return null

  try {
    return JSON.parse(cached)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[global-swatch-cache] Failed to parse cache', error)

    return null
  }
}

export const getLinkedProductsFromCache = (linkedCode, currentHandle) => {
  const parsed = getGlobalSwatchDatas()
  const group = parsed?.linkedGroup?.find(
    (item) =>
      item['linked-code'] === linkedCode ||
      item.products?.some((product) => product.handle === currentHandle)
  )

  return group?.products || []
}

/**
 * Builds sessionStorage globalSwatchDatas when missing.
 * Shares one in-flight promise so PDP / PLP / hooks don't double-fetch.
 */
export const ensureGlobalSwatchCache = async (force = false) => {
  if (!force && sessionStorage.getItem(GLOBAL_SWATCH_CACHE_KEY)) return

  if (cacheBuildPromise) {
    await cacheBuildPromise

    return
  }

  if (force) {
    sessionStorage.removeItem(GLOBAL_SWATCH_CACHE_KEY)
  }

  cacheBuildPromise = buildGlobalSwatchCache()
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('[global-swatch-cache] Failed to build cache', error)
    })
    .finally(() => {
      cacheBuildPromise = null
    })

  await cacheBuildPromise
}
